import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { StripeService } from '@functions/stripe.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select } from '@ngxs/store';
import { SnackbarService } from '@services/ui/snackbar.service';
import { BusinessState } from '@state/business';
import { UserSelectors } from '@state/global/user';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { Logger } from '@core/logger';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Business } from '@models/businesses';
import { LeapUrlService } from '../../leap-url.service';

enum StripeSetupStep {
	TRIAL_ENDED_NOTICE = 'trial_ended_notice',
	PROMPT_FOR_EMAIL_ADDR = 'prompt_for_email_addr',
}

@UntilDestroy()
@Component({
	selector: 'stripe-setup',
	templateUrl: './stripe-setup.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class StripeSetupComponent implements OnInit {
	@Select(UserSelectors.getCurrentUserIsAdmin)
	userIsAdmin$: Observable<boolean>;

	@Select(BusinessState.getHasActiveStripePaymentMethod)
	hasActivePaymentMethod$: Observable<boolean>;

	@SelectSnapshot(BusinessState.getBusiness) business: Business;

	@SelectSnapshot(BusinessState.getStripeBillingEmail)
	billingEmail: string;

	public currSetupStep$ = new BehaviorSubject<StripeSetupStep>(
		StripeSetupStep.TRIAL_ENDED_NOTICE,
	);
	public StripeSetupStep = StripeSetupStep;

	public emailControl = new FormControl('', [
		Validators.email,
		Validators.required,
	]);

	public isProcessing$ = new BehaviorSubject<boolean>(false);
	public emailControlValid$ = this.emailControl.statusChanges.pipe(
		untilDestroyed(this),
		startWith(this.emailControl.status),
		map((s) => s === 'VALID'),
	);
	public nextBtnDisabled$: Observable<boolean>;

	public createStripeCustomerError$ = new BehaviorSubject<string | null>(null);

	constructor(
		private leapUrlService: LeapUrlService,
		private stripeService: StripeService,
		private snackbarService: SnackbarService,
	) {}

	ngOnInit() {
		this.nextBtnDisabled$ = combineLatest([
			this.userIsAdmin$,
			this.hasActivePaymentMethod$,
			this.emailControlValid$,
		]).pipe(
			untilDestroyed(this),
			map(([userIsAdmin, hasActivePaymentMethod, emailValid]) => {
				return !userIsAdmin || hasActivePaymentMethod || !emailValid;
			}),
		);

		if (this.billingEmail) {
			this.emailControl.setValue(this.billingEmail);
		}
	}

	goToPromptForEmailAddrStep() {
		this.currSetupStep$.next(StripeSetupStep.PROMPT_FOR_EMAIL_ADDR);
	}

	goToTrialEndedNoticeStep() {
		this.currSetupStep$.next(StripeSetupStep.TRIAL_ENDED_NOTICE);
	}

	async saveEmail() {
		try {
			const email = this.emailControl.value?.trim();

			await this.stripeService.updateBillingEmailAddress(email).toPromise();

			return true;
		} catch (err) {
			this.snackbarService.openSnackBar({
				text: 'Unable to save billing email.',
				type: 'error',
			});

			Logger.error(
				`[Leap] Error saving billing email address for Stripe: ${err}`,
			);

			return false;
		}
	}

	async stripeCustomerSignup() {
		try {
			this.createStripeCustomerError$.next(null);

			await this.stripeService
				.createNewStripeCustomer(this.business.id)
				.toPromise();

			return true;
		} catch (err) {
			this.createStripeCustomerError$.next(`${err}`);

			Logger.error(`[Leap] Error creating new stripe customer: ${err}`);

			return false;
		}
	}

	async getBillingPortalRedirectURL(): Promise<string | boolean> {
		try {
			const initialUrl = this.leapUrlService.getFullInitialUrl();

			const stripeUrl = await this.stripeService
				.getBillingPortalRedirectURL(initialUrl)
				.toPromise();

			if (stripeUrl) {
				return stripeUrl;
			} else {
				throw new Error('No URL returned from Stripe');
			}
		} catch (err) {
			Logger.error(`[Leap] Error getting stripe redirect session url: ${err}`);

			return false;
		}
	}

	async navigateToStripe() {
		this.isProcessing$.next(true);

		const saveEmailResult = await this.saveEmail();

		if (!saveEmailResult) {
			return;
		}

		if (!this.business.stripeCustomerId) {
			const signupResult = await this.stripeCustomerSignup();

			if (!signupResult) {
				return;
			}
		}

		const stripeUrl = await this.getBillingPortalRedirectURL();

		if (stripeUrl) {
			window.location.href = stripeUrl as string;
		} else {
			this.isProcessing$.next(false);
		}
	}
}
